import { WORK_QUEUE_POLL_MS } from '~/data/hooks/useActionItems';
import { useInboxActionItems } from '~/data/hooks/useInboxActionItems';

// The server's `max_page_size`.
const PAGE_SIZE = 200;

/**
 * The work queue narrowed to one record. Both scopes are server filters: an `ActionItem`
 * has no car, so `?car=` resolves each family back to the cars it touches (the invoice's
 * `cars_serviced`, the contract's `PropertyContractCar`, the certificate's own car).
 * Filtering on the client would only ever find the compliance items.
 */
export function useNeedsAttentionItems(propertyId: string | undefined, carId?: string) {
  const { actionItems, count, isLoading, isValidating, snooze, refresh } = useInboxActionItems(
    {
      ...(propertyId && { property: propertyId }),
      ...(carId && { car: carId }),
      pageSize: PAGE_SIZE,
    },
    { refreshInterval: WORK_QUEUE_POLL_MS, loadAll: true },
  );

  return {
    items: actionItems,
    // Undefined until the read lands, so the tab label shows nothing rather than a zero.
    count: isLoading ? undefined : count,
    isLoading,
    isValidating,
    snooze,
    refresh,
  };
}

/** How many items {@link useNeedsAttentionItems} would list: one row's read, no polling. */
export function useNeedsAttentionCount(propertyId: string | undefined, carId?: string) {
  const { count, isLoading } = useInboxActionItems({
    ...(propertyId && { property: propertyId }),
    ...(carId && { car: carId }),
    pageSize: 1,
  });
  return isLoading ? undefined : count;
}
